import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Fab, MenuItem, Stack, TextField, Tooltip, Typography, Zoom,
} from '@mui/material';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ENDPOINTS, apiCall } from '../utils/api.js';

// Build-time feature flag. The button is hidden unless this is explicitly enabled.
// Set REACT_APP_FEEDBACK_ENABLED="false" to turn it off.
const FEEDBACK_ENABLED = String(process.env.REACT_APP_FEEDBACK_ENABLED ?? 'true').toLowerCase() !== 'false';

const CATEGORIES = [
  { value: 'bug', label: '🐞 Something is broken' },
  { value: 'idea', label: '💡 Idea / suggestion' },
  { value: 'question', label: '❓ Question' },
  { value: 'other', label: '💬 Other' },
];

function isLoggedIn() {
  return !!localStorage.getItem('accessToken');
}

/**
 * Floating "Feedback" button, shown only to logged-in users and only when the
 * REACT_APP_FEEDBACK_ENABLED flag is on. Opens a small form; on submit it POSTs
 * the message plus debug context (page URL, user agent) to /api/feedback/, which
 * emails the support inbox. The backend adds the authenticated user's details.
 */
export default function FeedbackButton() {
  const location = useLocation();
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Keep visibility in sync with login/logout. The app fires 'sqip:logout' on sign-out
  // and 'sqip:loggedIn' on sign-in; 'storage' covers other tabs. If the dialog is open
  // when the user logs out, close it too.
  useEffect(() => {
    const sync = () => {
      const li = isLoggedIn();
      setLoggedIn(li);
      if (!li) setOpen(false);
    };
    const events = ['storage', 'sqip:tokenRefreshed', 'sqip:loggedIn', 'sqip:logout', 'sqip:loggedOut'];
    events.forEach((e) => window.addEventListener(e, sync));
    return () => events.forEach((e) => window.removeEventListener(e, sync));
  }, []);

  // Re-check on every route change — 401 auto-logouts just clear storage and
  // navigate('/') without firing an event, so this catches those too.
  useEffect(() => {
    const id = setTimeout(() => {   // deferred: avoid setState synchronously in an effect
      const li = isLoggedIn();
      setLoggedIn(li);
      if (!li) setOpen(false);
    }, 0);
    return () => clearTimeout(id);
  }, [location.pathname]);

  const reset = useCallback(() => {
    setMessage(''); setCategory('bug'); setError(''); setDone(false); setBusy(false);
  }, []);

  const handleOpen = () => { reset(); setOpen(true); };
  const handleClose = () => { if (!busy) setOpen(false); };

  const submit = async () => {
    if (message.trim().length < 5) {
      setError('Please add a little more detail (at least 5 characters).');
      return;
    }
    setBusy(true); setError('');
    try {
      await apiCall(ENDPOINTS.FEEDBACK, {
        method: 'POST',
        body: JSON.stringify({
          category,
          message: message.trim(),
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          app_version: process.env.REACT_APP_VERSION || '',
        }),
      });
      setDone(true);
    } catch (e) {
      setError((e && (e.message || (e.body && e.body.detail))) || 'Could not send feedback. Please try again.');
    }
    setBusy(false);
  };

  if (!FEEDBACK_ENABLED || !loggedIn) return null;

  return (
    <>
      <Zoom in>
        <Tooltip title="Send feedback" placement="left">
          <Fab
            color="primary"
            size="medium"
            onClick={handleOpen}
            aria-label="Send feedback"
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              zIndex: (t) => t.zIndex.speedDial,
              textTransform: 'none',
            }}
          >
            <FeedbackOutlinedIcon />
          </Fab>
        </Tooltip>
      </Zoom>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        {done ? (
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>Thanks for the feedback!</Typography>
            <Typography variant="body2" color="text.secondary">
              We've received it and will take a look. You can reach us any time at support@sqipqueue.com.
            </Typography>
            <Button onClick={() => setOpen(false)} variant="contained" sx={{ mt: 2, textTransform: 'none' }}>
              Done
            </Button>
          </DialogContent>
        ) : (
          <>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Send feedback
              <Button onClick={handleClose} disabled={busy} sx={{ minWidth: 0, p: 0.5, color: 'text.secondary' }}>
                <CloseIcon fontSize="small" />
              </Button>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Found a bug or have an idea? Tell us what happened — we automatically include
                  your account and page details to help us debug.
                </Typography>
                <TextField
                  select fullWidth size="small" label="Type"
                  value={category} onChange={(e) => setCategory(e.target.value)}
                >
                  {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
                <TextField
                  fullWidth multiline minRows={4} autoFocus label="Your feedback"
                  placeholder="Describe the issue or idea…"
                  value={message} onChange={(e) => setMessage(e.target.value)}
                  inputProps={{ maxLength: 4000 }}
                />
                {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                variant="contained" onClick={submit} disabled={busy || !message.trim()}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {busy ? <CircularProgress size={18} color="inherit" /> : 'Send feedback'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
